import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import Editor from "@monaco-editor/react";
import SidebarLayout from "../../shared/sections/sidebar/SidebarLayout";
import { apiFetch } from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";

const API_BASE = "/api/admin";

const SchemaManagementV2Page = () => {
  const { user } = useAuth();
  // State management
  const [towers, setTowers] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [selectedTower, setSelectedTower] = useState("");
  const [selectedCommodity, setSelectedCommodity] = useState("");
  const [schemaTypes, setSchemaTypes] = useState([]);
  const [selectedSchemaType, setSelectedSchemaType] = useState(null);
  const [schemaCache, setSchemaCache] = useState({}); // Cache all loaded schemas
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Schema content state
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPromptTemplate, setUserPromptTemplate] = useState("");
  const [outputSchema, setOutputSchema] = useState("");
  const [modelConfig, setModelConfig] = useState({
    model: "gpt-4o",
    temperature: 0.7,
    max_tokens: 4000,
  });
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [jsonPrompt, setJsonPrompt] = useState("");
  const [constraintsJson, setConstraintsJson] = useState("");

  // AI Refine state
  const [refineDialogOpen, setRefineDialogOpen] = useState(false);
  const [refineType, setRefineType] = useState(""); // "system" or "user"
  const [refining, setRefining] = useState(false);
  const [aiVariants, setAiVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [currentPromptText, setCurrentPromptText] = useState("");

  // Editor tabs (0=System Prompt, 1=User Prompt, 2=Output Schema, 3=Model Config, 4=JSON Prompt, 5=Constraints)
  const [editorTab, setEditorTab] = useState(0);
  const [schemaViewMode, setSchemaViewMode] = useState("code"); // "code" or "card"

  // Versions and audit
  const [versions, setVersions] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // 0=Editor, 1=Versions, 2=Audit

  // Constraint Rules state (for Constraints tab)
  const [constraintRules, setConstraintRules] = useState([]);
  const [selectedConstraintRule, setSelectedConstraintRule] = useState(null);
  const [loadingConstraintRules, setLoadingConstraintRules] = useState(false);
  const [constraintViewMode, setConstraintViewMode] = useState("json"); // "json" or "card"
  const [editingSection, setEditingSection] = useState(null); // Track which section is being edited
  const [editedConstraints, setEditedConstraints] = useState({}); // Store edited constraint data
  const [editingRule, setEditingRule] = useState(false); // Track if editing entire rule
  const [editedRuleData, setEditedRuleData] = useState(null); // Store edited rule data
  const [editingJsonView, setEditingJsonView] = useState(false); // Track if editing JSON view
  const [editedJsonContent, setEditedJsonContent] = useState(""); // Store edited JSON content
  const [newRuleDialogOpen, setNewRuleDialogOpen] = useState(false);
  const [newRuleData, setNewRuleData] = useState({
    priority: 100,
    active: true,
    condition_json: {},
    constraints_json: {},
    notes: "",
  });

  // Dialogs
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newVersion, setNewVersion] = useState("");
  const [versionDescription, setVersionDescription] = useState("");
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [versionToActivate, setVersionToActivate] = useState(null);

  // Execution Group editing
  const [editingExecutionGroup, setEditingExecutionGroup] = useState(null); // schema_type being edited
  const [executionGroupValue, setExecutionGroupValue] = useState("");
  const [executionGroupDialogOpen, setExecutionGroupDialogOpen] = useState(false);
  const [tempSchemaGroups, setTempSchemaGroups] = useState([]);
  const [savingGroups, setSavingGroups] = useState(false);

  const normalizedRole = (() => {
    if (Array.isArray(user?.role) && user.role.length > 0) {
      return String(user.role[0]?.role_name || "")
        .trim()
        .toLowerCase();
    }
    return String(user?.role || "")
      .trim()
      .toLowerCase();
  })();

  const isSuperAdmin = normalizedRole === "super admin";

  // Load towers on mount
  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchTowers();
  }, [isSuperAdmin]);

  // Load commodities when tower changes
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (selectedTower) {
      setCommodities([]);
      setSelectedCommodity(null);
      setSchemaTypes([]);
      setSelectedSchemaType(null);
      fetchCommodities(selectedTower);
    }
  }, [selectedTower, isSuperAdmin]);

  // Load schema types when commodity changes
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (selectedTower && selectedCommodity) {
      setSchemaTypes([]);
      setSelectedSchemaType(null);
      fetchSchemaTypes();
    }
  }, [selectedTower, selectedCommodity, isSuperAdmin]);

  // API calls
  const fetchTowers = async () => {
    try {
      const response = await apiFetch(`${API_BASE}/towers`);
      const data = await response.json();
      setTowers(data.towers || []);
    } catch (error) {
      setError("Failed to load towers");
    }
  };

  const fetchCommodities = async (tower) => {
    try {
      const response = await apiFetch(`${API_BASE}/commodities?tower=${tower}`);
      const data = await response.json();
      setCommodities(data.commodities || []);
    } catch (error) {
      setError("Failed to load commodities");
    }
  };

  const fetchSchemaTypes = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(
        `${API_BASE}/schema-v2/types?tower=${selectedTower}&commodity=${selectedCommodity}`,
      );
      const data = await response.json();
      const types = data.schema_types || [];

      // Sort by execution_group in ascending order
      const sortedTypes = types.sort((a, b) => {
        const groupA = a.execution_group || 999;
        const groupB = b.execution_group || 999;
        return groupA - groupB;
      });

      setSchemaTypes(sortedTypes);

      // Preload all schemas into cache
      const cache = {};
      await Promise.all(
        types.map(async (schemaType) => {
          try {
            const schemaResponse = await apiFetch(
              `${API_BASE}/schema-v2/${selectedTower}/${selectedCommodity}/${schemaType.schema_type}`,
            );
            const schemaData = await schemaResponse.json();
            if (schemaData.success) {
              cache[schemaType.schema_type] = schemaData.schema;
            }
          } catch (err) {
            console.error(`Failed to preload ${schemaType.schema_type}:`, err);
          }
        }),
      );
      setSchemaCache(cache);
      setError(null);
    } catch (error) {
      setError("Failed to load schema types");
    } finally {
      setLoading(false);
    }
  };

  const loadSchemaType = async (schemaType) => {
    try {
      // Check if schema is already in cache
      const cachedSchema = schemaCache[schemaType];

      if (cachedSchema) {
        // Use cached data - no loading indicator needed
        setSelectedSchemaType(schemaType);
        setSystemPrompt(cachedSchema.system_prompt || "");
        setUserPromptTemplate(cachedSchema.user_template || "");
        setOutputSchema(
          typeof cachedSchema.schema_json === "string"
            ? cachedSchema.schema_json
            : JSON.stringify(cachedSchema.schema_json, null, 2),
        );
        setModelConfig({
          model: cachedSchema.model || "gpt-4o",
          temperature: cachedSchema.temperature || 0.7,
          max_tokens: cachedSchema.max_tokens || 4000,
        });
        setDescription(cachedSchema.description || "");
        setNotes(cachedSchema.notes || "");
        setJsonPrompt(cachedSchema.image_prompt_json || "");
        setConstraintsJson(cachedSchema.constraints_json || "");

        // Load versions and audit log in background
        loadVersions(schemaType);
        loadAuditLog(schemaType);

        // Reload constraint rules if Constraints tab is currently active
        if (editorTab === 5) {
          setSelectedConstraintRule(null); // Reset selected rule
          loadConstraintRules(schemaType);
        }

        setError(null);
      } else {
        // Fallback: fetch from server if not in cache
        setLoading(true);
        const response = await apiFetch(
          `${API_BASE}/schema-v2/${selectedTower}/${selectedCommodity}/${schemaType}`,
        );
        const data = await response.json();

        if (data.success) {
          // Update cache
          setSchemaCache((prev) => ({ ...prev, [schemaType]: data.schema }));

          setSelectedSchemaType(schemaType);
          setSystemPrompt(data.schema.system_prompt || "");
          setUserPromptTemplate(data.schema.user_template || "");
          setOutputSchema(
            typeof data.schema.schema_json === "string"
              ? data.schema.schema_json
              : JSON.stringify(data.schema.schema_json, null, 2),
          );
          setModelConfig({
            model: data.schema.model || "gpt-4o",
            temperature: data.schema.temperature || 0.7,
            max_tokens: data.schema.max_tokens || 4000,
          });
          setDescription(data.schema.description || "");
          setNotes(data.schema.notes || "");
          setJsonPrompt(data.schema.image_prompt_json || "");
          setConstraintsJson(data.schema.constraints_json || "");

          loadVersions(schemaType);
          loadAuditLog(schemaType);

          // Reload constraint rules if Constraints tab is currently active
          if (editorTab === 5) {
            setSelectedConstraintRule(null); // Reset selected rule
            loadConstraintRules(schemaType);
          }

          setError(null);
        }
        setLoading(false);
      }
    } catch (error) {
      setError(`Failed to load schema: ${error.message}`);
      setLoading(false);
    }
  };

  const loadVersions = async (schemaType) => {
    try {
      const response = await apiFetch(
        `${API_BASE}/schema-v2/${selectedTower}/${selectedCommodity}/${schemaType}/versions`,
      );
      const data = await response.json();
      setVersions(data.versions || []);
    } catch (error) {
      console.error("Failed to load versions:", error);
    }
  };

  const loadAuditLog = async (schemaType) => {
    try {
      const response = await apiFetch(
        `${API_BASE}/schema-v2/${selectedTower}/${selectedCommodity}/${schemaType}/audit`,
      );
      const data = await response.json();
      setAuditLog(data.audit_log || []);
    } catch (error) {
      console.error("Failed to load audit log:", error);
    }
  };

  const updateExecutionGroup = async (schemaType, newGroup) => {
    try {
      const response = await apiFetch(
        `${API_BASE}/schema-v2/${selectedTower}/${selectedCommodity}/${schemaType}/execution-group`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ execution_group: parseInt(newGroup) }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setSuccess("Execution group updated successfully");
        // Refresh schema types to get new sorting
        await fetchSchemaTypes();
        setEditingExecutionGroup(null);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || "Failed to update execution group");
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      setError(`Failed to update execution group: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleExecutionGroupClick = (schemaType, currentGroup) => {
    setEditingExecutionGroup(schemaType);
    setExecutionGroupValue(currentGroup?.toString() || "");
  };

  const handleExecutionGroupSave = async (schemaType) => {
    if (executionGroupValue && !isNaN(executionGroupValue)) {
      await updateExecutionGroup(schemaType, executionGroupValue);
    } else {
      setEditingExecutionGroup(null);
    }
  };

  const handleExecutionGroupKeyPress = (e, schemaType) => {
    if (e.key === "Enter") {
      handleExecutionGroupSave(schemaType);
    } else if (e.key === "Escape") {
      setEditingExecutionGroup(null);
    }
  };

  const openExecutionGroupDialog = () => {
    // Create a copy of schemaTypes with editable groups
    const groups = schemaTypes.map((s, index) => ({
      schema_type: s.schema_type,
      execution_group: s.execution_group || 999,
      original_order: index,
      description: s.description || "",
    }));
    setTempSchemaGroups(groups);
    setExecutionGroupDialogOpen(true);
  };

  const handleGroupChange = (schemaType, newGroup) => {
    setTempSchemaGroups((prev) =>
      prev.map((s) =>
        s.schema_type === schemaType ? { ...s, execution_group: parseInt(newGroup) || 1 } : s,
      ),
    );
  };

  const moveSchemaUp = (index) => {
    if (index === 0) return;
    setTempSchemaGroups((prev) => {
      const newGroups = [...prev];
      [newGroups[index - 1], newGroups[index]] = [newGroups[index], newGroups[index - 1]];
      return newGroups;
    });
  };

  const moveSchemaDown = (index) => {
    if (index === tempSchemaGroups.length - 1) return;
    setTempSchemaGroups((prev) => {
      const newGroups = [...prev];
      [newGroups[index], newGroups[index + 1]] = [newGroups[index + 1], newGroups[index]];
      return newGroups;
    });
  };

  const autoAssignGroups = () => {
    // Assign sequential groups based on current order
    const groupSize = Math.ceil(tempSchemaGroups.length / 5); // Divide into ~5 groups
    setTempSchemaGroups((prev) =>
      prev.map((s, index) => ({
        ...s,
        execution_group: Math.floor(index / groupSize) + 1,
      })),
    );
  };

  const saveExecutionGroups = async () => {
    setSavingGroups(true);
    try {
      // Send updates to backend for each schema
      const updates = tempSchemaGroups.map(async (schema) => {
        const response = await apiFetch(
          `${API_BASE}/schema-v2/${selectedTower}/${selectedCommodity}/${schema.schema_type}/execution-group`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ execution_group: schema.execution_group }),
          },
        );

        if (!response.ok) {
          const text = await response.text();
          console.error(`Failed to update ${schema.schema_type}:`, text);
          throw new Error(`Failed to update ${schema.schema_type}: ${response.status}`);
        }

        return response.json();
      });

      const results = await Promise.all(updates);
      console.log("All updates completed:", results);

      setSuccess("Execution groups updated successfully");
      setExecutionGroupDialogOpen(false);

      // Refresh schema types to get new sorting
      await fetchSchemaTypes();

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Error updating execution groups:", error);
      setError(`Failed to update execution groups: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSavingGroups(false);
    }
  };

  const loadConstraintRules = async (schemaType) => {
    setLoadingConstraintRules(true);
    console.log(
      `🔍 Loading constraint rules for: tower=${selectedTower}, commodity=${selectedCommodity}, schema_type=${schemaType}`,
    );
    try {
      const url = `${API_BASE}/constraint-rules?tower=${selectedTower}&commodity=${selectedCommodity}&schema_type=${schemaType}`;
      console.log(`📡 Fetching from: ${url}`);
      const response = await apiFetch(url);
      const data = await response.json();
      console.log(`📦 Received data:`, data);
      const rules = data.rules || [];
      console.log(`✅ Loaded ${rules.length} constraint rules`);
      setConstraintRules(rules);
      // Auto-select first rule if available
      if (rules.length > 0 && !selectedConstraintRule) {
        setSelectedConstraintRule(rules[0]);
        console.log(`🎯 Auto-selected rule:`, rules[0]);
      }
    } catch (error) {
      console.error("❌ Failed to load constraint rules:", error);
      setConstraintRules([]);
    } finally {
      setLoadingConstraintRules(false);
    }
  };

  const saveRuleEdit = async () => {
    try {
      setError(null);
      setSuccess(null);

      const payload = {
        priority: editedRuleData.priority,
        is_active: editedRuleData.active,
        condition_json: editedRuleData.condition_json,
      };

      console.log(`💾 Saving rule ${selectedConstraintRule.rule_id}:`, payload);

      const response = await apiFetch(
        `${API_BASE}/constraint-rules/${selectedConstraintRule.rule_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (data.status === "success") {
        setSuccess("Rule updated successfully");
        setEditingRule(false);
        setEditedRuleData(null);
        // Reload rules to get updated data
        await loadConstraintRules(selectedSchemaType);
        // Update selected rule
        const updatedRule = constraintRules.find(
          (r) => r.rule_id === selectedConstraintRule.rule_id,
        );
        if (updatedRule) {
          setSelectedConstraintRule(updatedRule);
        }
      } else {
        setError(data.error || "Failed to update rule");
      }
    } catch (error) {
      console.error("❌ Error saving rule:", error);
      setError(`Failed to save rule: ${error.message}`);
    }
  };

  const saveConstraintSection = async (sectionKey) => {
    try {
      setError(null);
      setSuccess(null);

      const payload = {
        section_key: sectionKey,
        section_value: editedConstraints[sectionKey],
      };

      console.log(
        `💾 Saving constraint section ${sectionKey} for rule ${selectedConstraintRule.rule_id}:`,
        payload,
      );

      const response = await apiFetch(
        `${API_BASE}/constraint-rules/${selectedConstraintRule.rule_id}/constraints`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (data.status === "success") {
        setSuccess(`Section ${sectionKey} updated successfully`);
        setEditingSection(null);
        setEditedConstraints({});
        // Reload rules to get updated data
        await loadConstraintRules(selectedSchemaType);
        // Update selected rule
        const updatedRule = constraintRules.find(
          (r) => r.rule_id === selectedConstraintRule.rule_id,
        );
        if (updatedRule) {
          setSelectedConstraintRule(updatedRule);
        }
      } else {
        setError(data.error || "Failed to update constraint section");
      }
    } catch (error) {
      console.error("❌ Error saving constraint section:", error);
      setError(`Failed to save section: ${error.message}`);
    }
  };

  const createNewRule = async () => {
    try {
      setError(null);
      setSuccess(null);

      const payload = {
        tower: selectedTower,
        commodity: selectedCommodity,
        schema_type: selectedSchemaType,
        priority: newRuleData.priority,
        is_active: newRuleData.active,
        condition_json: newRuleData.condition_json,
        constraints_json: newRuleData.constraints_json,
        notes: newRuleData.notes,
      };

      console.log(`💾 Creating new rule:`, payload);

      const response = await apiFetch(`${API_BASE}/constraint-rules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status === "success") {
        setSuccess("Rule created successfully");
        setNewRuleDialogOpen(false);
        setNewRuleData({
          priority: 100,
          active: true,
          condition_json: {},
          constraints_json: {},
          notes: "",
        });
        // Reload rules
        await loadConstraintRules(selectedSchemaType);
      } else {
        setError(data.error || "Failed to create rule");
      }
    } catch (error) {
      console.error("❌ Error creating rule:", error);
      setError(`Failed to create rule: ${error.message}`);
    }
  };

  const handleUpdateSchema = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Parse output schema to validate JSON
      let schemaJson;
      try {
        schemaJson = JSON.parse(outputSchema);
      } catch (e) {
        throw new Error("Invalid JSON in Output Schema");
      }

      const payload = {
        tower: selectedTower,
        commodity: selectedCommodity,
        schema_type: selectedSchemaType,
        schema_json: schemaJson,
        system_prompt: systemPrompt,
        user_template: userPromptTemplate,
        model: modelConfig.model,
        temperature: parseFloat(modelConfig.temperature),
        max_tokens: modelConfig.max_tokens ? parseInt(modelConfig.max_tokens) : null,
        description: description,
        notes: notes,
        image_prompt_json: jsonPrompt,
        constraints_json: constraintsJson,
      };

      const response = await apiFetch(`${API_BASE}/schema-v2/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess("Schema updated successfully");
        // Update cache with new data
        setSchemaCache((prev) => ({
          ...prev,
          [selectedSchemaType]: {
            system_prompt: systemPrompt,
            user_template: userPromptTemplate,
            schema_json: schemaJson,
            model: modelConfig.model,
            temperature: modelConfig.temperature,
            max_tokens: modelConfig.max_tokens,
            description: description,
            notes: notes,
            image_prompt_json: jsonPrompt,
            constraints_json: constraintsJson,
          },
        }));
        loadAuditLog(selectedSchemaType);
      } else {
        throw new Error(data.error || "Failed to update schema");
      }
    } catch (error) {
      setError(`Update failed: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSchema = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Parse output schema to validate JSON
      let schemaJson;
      try {
        schemaJson = JSON.parse(outputSchema);
      } catch (e) {
        throw new Error("Invalid JSON in Output Schema");
      }

      const payload = {
        tower: selectedTower,
        commodity: selectedCommodity,
        schema_type: selectedSchemaType,
        schema_version: parseInt(newVersion) || 1,
        schema_json: schemaJson,
        system_prompt: systemPrompt,
        user_template: userPromptTemplate,
        model: modelConfig.model,
        temperature: parseFloat(modelConfig.temperature),
        max_tokens: modelConfig.max_tokens ? parseInt(modelConfig.max_tokens) : null,
        description: versionDescription || description,
        notes: notes,
        is_active: false, // New versions are inactive by default
        image_prompt_json: jsonPrompt,
        constraints_json: constraintsJson,
      };

      const response = await apiFetch(`${API_BASE}/schema-v2/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Schema saved successfully as version ${newVersion}`);
        setSaveDialogOpen(false);
        setNewVersion("");
        setVersionDescription("");
        loadVersions(selectedSchemaType);
        loadAuditLog(selectedSchemaType);
      } else {
        throw new Error(data.error || "Failed to save schema");
      }
    } catch (error) {
      setError(`Save failed: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRefinePrompt = async () => {
    setRefining(true);
    try {
      if (!selectedVariant) {
        setError("Please select a variant to apply");
        setRefining(false);
        return;
      }

      // Apply the selected variant directly
      if (refineType === "system") {
        setSystemPrompt(selectedVariant);
      } else {
        setUserPromptTemplate(selectedVariant);
      }

      setSuccess("Prompt variant applied successfully");
      setRefineDialogOpen(false);
      setAiVariants([]);
      setSelectedVariant(null);
    } catch (error) {
      setError(`Apply failed: ${error.message}`);
    } finally {
      setRefining(false);
    }
  };

  const loadAiVariants = async (promptText, type) => {
    setLoadingVariants(true);
    try {
      const response = await apiFetch(`${API_BASE}/suggest-refinements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: promptText,
          promptType: type,
          schemaType: selectedSchemaType,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setAiVariants(data.suggestions || []);
      } else {
        throw new Error(data.error || "Failed to load variants");
      }
    } catch (error) {
      setError(`Failed to load variants: ${error.message}`);
      setAiVariants([]);
    } finally {
      setLoadingVariants(false);
    }
  };

  const openRefineDialog = (type) => {
    const promptText = type === "system" ? systemPrompt : userPromptTemplate;
    setRefineType(type);
    setCurrentPromptText(promptText);
    setRefineDialogOpen(true);
    setAiVariants([]);
    setSelectedVariant(null);

    // Load AI variants automatically
    loadAiVariants(promptText, type);
  };

  const handleActivateVersion = async () => {
    try {
      const response = await apiFetch(`${API_BASE}/schema-v2/activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tower: selectedTower,
          commodity: selectedCommodity,
          schema_type: selectedSchemaType,
          schema_version: versionToActivate,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Version ${versionToActivate} activated successfully`);
        setActivateDialogOpen(false);
        setVersionToActivate(null);
        loadVersions(selectedSchemaType);
        loadAuditLog(selectedSchemaType);
        loadSchemaType(selectedSchemaType); // Reload to show active version
      } else {
        throw new Error(data.error || "Failed to activate version");
      }
    } catch (error) {
      setError(`Activation failed: ${error.message}`);
    }
  };

  const openSaveDialog = () => {
    // Auto-increment version
    const maxVersion = versions.reduce((max, v) => Math.max(max, v.schema_version), 0);
    setNewVersion((maxVersion + 1).toString());
    setSaveDialogOpen(true);
  };

  if (!isSuperAdmin) {
    return (
      <SidebarLayout>
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning">This page is available to Super Admin only.</Alert>
        </Box>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <Box sx={{ display: "flex", height: "100%", bgcolor: "background.default" }}>
        {/* Left Navigation - Schema Types */}
        <Paper
          sx={{
            width: 280,
            borderRadius: 0,
            borderRight: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Tower & Commodity Selection */}
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Schema Management V2
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Tower</InputLabel>
              <Select
                value={selectedTower}
                onChange={(e) => {
                  setSelectedTower(e.target.value);
                  setSelectedCommodity("");
                  setSchemaTypes([]);
                  setSelectedSchemaType(null);
                }}
                label="Tower"
              >
                {towers.map((tower) => (
                  <MenuItem key={tower.tower_code} value={tower.tower_code}>
                    {tower.tower_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!selectedTower}>
              <InputLabel>Commodity</InputLabel>
              <Select
                value={selectedCommodity}
                onChange={(e) => {
                  setSelectedCommodity(e.target.value);
                  setSelectedSchemaType(null);
                }}
                label="Commodity"
              >
                {commodities.map((commodity) => (
                  <MenuItem key={commodity.commodity_code} value={commodity.commodity_code}>
                    {commodity.commodity_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Schema Types List Header with Manage Button */}
          {schemaTypes.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1,
                borderTop: 1,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                SCHEMAS ({schemaTypes.length})
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={openExecutionGroupDialog}
                sx={{ fontSize: "0.7rem", py: 0.5, px: 1 }}
              >
                Manage Groups
              </Button>
            </Box>
          )}

          {/* Schema Types List */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {!loading && schemaTypes.length === 0 && selectedCommodity && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No schemas found
                </Typography>
              </Box>
            )}

            <List sx={{ p: 0 }}>
              {schemaTypes.map((schemaType) => (
                <ListItem key={schemaType.schema_type} disablePadding>
                  <ListItemButton
                    selected={selectedSchemaType === schemaType.schema_type}
                    onClick={() => loadSchemaType(schemaType.schema_type)}
                    sx={{
                      py: 1,
                      px: 2,
                      borderLeft: 3,
                      borderColor:
                        selectedSchemaType === schemaType.schema_type
                          ? "primary.main"
                          : "transparent",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: selectedSchemaType === schemaType.schema_type ? 600 : 400,
                              flex: 1,
                            }}
                          >
                            {schemaType.schema_type}
                          </Typography>
                          <Chip
                            label={`${schemaType.execution_group || "?"}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              bgcolor:
                                selectedSchemaType === schemaType.schema_type
                                  ? "primary.main"
                                  : "action.selected",
                              color:
                                selectedSchemaType === schemaType.schema_type
                                  ? "primary.contrastText"
                                  : "text.secondary",
                            }}
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Paper>

        {/* Main Content Area */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Top Bar */}
          <Paper
            sx={{
              px: 2,
              py: 0.5,
              borderRadius: 0,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {selectedTower && selectedCommodity && (
                <Box
                  component="img"
                  src={`/images/Commodities/${
                    selectedCommodity.charAt(0).toUpperCase() +
                    selectedCommodity.slice(1).toLowerCase()
                  }.jpg`}
                  alt={selectedCommodity}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    objectFit: "cover",
                    border: 1,
                    borderColor: "divider",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                {selectedTower && selectedCommodity
                  ? `${selectedTower}/${selectedCommodity}`
                  : "Select a Schema"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="Refresh Schema">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => selectedSchemaType && loadSchemaType(selectedSchemaType)}
                    disabled={!selectedSchemaType}
                    sx={{
                      "&:disabled": {
                        bgcolor: "action.disabledBackground",
                        color: "action.disabled",
                      },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Save (Update Current Version)">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleUpdateSchema}
                    disabled={!selectedSchemaType || saving}
                    sx={{
                      "&:disabled": {
                        bgcolor: "action.disabledBackground",
                        color: "action.disabled",
                      },
                    }}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Save New Version">
                <span>
                  <IconButton
                    size="small"
                    onClick={openSaveDialog}
                    disabled={!selectedSchemaType || saving}
                    sx={{
                      "&:disabled": {
                        bgcolor: "action.disabledBackground",
                        color: "action.disabled",
                      },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Paper>

          {/* Content Area */}
          {loading ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress size={60} />
            </Box>
          ) : selectedSchemaType ? (
            <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
              {/* Left Panel - Editor */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  borderRight: 1,
                  borderColor: "divider",
                }}
              >
                <Tabs
                  value={editorTab}
                  onChange={(e, v) => {
                    setEditorTab(v);
                    // Load constraint rules when Constraints tab is selected
                    if (v === 5 && selectedSchemaType) {
                      loadConstraintRules(selectedSchemaType);
                    }
                  }}
                  sx={{ borderBottom: 1, borderColor: "divider" }}
                >
                  <Tab label="System Prompt" />
                  <Tab label="User Prompt" />
                  <Tab label="Output Schema" />
                  <Tab label="Model Config" />
                  <Tab label="JSON Prompt" />
                  <Tab label="Constraints" />
                </Tabs>

                <Box sx={{ flex: 1, overflow: "auto", p: 2, bgcolor: "background.default" }}>
                  {editorTab === 0 && (
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          System Prompt
                        </Typography>
                        <Tooltip title="AI Refine">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openRefineDialog("system")}
                              disabled={!systemPrompt}
                              sx={{
                                color: "white",
                                "&:disabled": {
                                  bgcolor: "action.disabledBackground",
                                  color: "action.disabled",
                                },
                              }}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={25}
                        value={systemPrompt}
                        onChange={(e) => setSystemPrompt(e.target.value)}
                        placeholder="Enter system prompt..."
                        variant="outlined"
                        spellCheck={false}
                        sx={{
                          "& .MuiInputBase-root": {
                            fontFamily: "monospace",
                            fontSize: 13,
                            bgcolor: "background.paper",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "divider",
                            },
                            "&:hover fieldset": {
                              borderColor: "divider",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "divider",
                            },
                          },
                          "& textarea": {
                            background: `linear-gradient(to right, 
                            transparent 0%, 
                            transparent calc(100% - 1px), 
                            transparent 100%
                          )`,
                            "&::selection": {
                              backgroundColor: "rgba(255, 255, 255, 0.2)",
                            },
                          },
                        }}
                        InputProps={{
                          style: {
                            color: "inherit",
                          },
                          inputComponent: "textarea",
                          inputProps: {
                            style: {
                              whiteSpace: "pre-wrap",
                              wordWrap: "break-word",
                            },
                          },
                        }}
                      />
                    </Box>
                  )}

                  {editorTab === 1 && (
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          User Prompt Template
                        </Typography>
                        <Tooltip title="AI Refine">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openRefineDialog("user")}
                              disabled={!userPromptTemplate}
                              sx={{
                                color: "white",
                                "&:disabled": {
                                  bgcolor: "action.disabledBackground",
                                  color: "action.disabled",
                                },
                              }}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={25}
                        value={userPromptTemplate}
                        onChange={(e) => setUserPromptTemplate(e.target.value)}
                        placeholder="Enter user prompt template..."
                        variant="outlined"
                        spellCheck={false}
                        sx={{
                          "& .MuiInputBase-root": {
                            fontFamily: "monospace",
                            fontSize: 13,
                            bgcolor: "background.paper",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "divider",
                            },
                            "&:hover fieldset": {
                              borderColor: "divider",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "divider",
                            },
                          },
                        }}
                        helperText={
                          <Typography
                            variant="caption"
                            sx={{ color: "warning.main", fontSize: "0.7rem" }}
                          >
                            Variables in {"{curly braces}"} will be replaced at runtime
                          </Typography>
                        }
                      />
                    </Box>
                  )}

                  {editorTab === 2 && (
                    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                      {/* View Mode Toggle */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          mb: 1,
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                        >
                          {selectedSchemaType || "Output Schema"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Code View">
                            <IconButton
                              size="small"
                              onClick={() => setSchemaViewMode("code")}
                              sx={{
                                color: schemaViewMode === "code" ? "white" : "text.disabled",
                                "&:disabled": {
                                  bgcolor: "action.disabledBackground",
                                  color: "action.disabled",
                                },
                              }}
                            >
                              <CodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Card View">
                            <IconButton
                              size="small"
                              onClick={() => setSchemaViewMode("card")}
                              sx={{
                                color: schemaViewMode === "card" ? "white" : "text.disabled",
                                "&:disabled": {
                                  bgcolor: "action.disabledBackground",
                                  color: "action.disabled",
                                },
                              }}
                            >
                              <ViewModuleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Code View */}
                      {schemaViewMode === "code" && (
                        <Box sx={{ flex: 1, overflow: "hidden" }}>
                          <Editor
                            key={`output-schema-${selectedSchemaType || "new"}`}
                            height="100%"
                            language="json"
                            theme="vs-dark"
                            defaultValue={outputSchema || ""}
                            onChange={(value) => setOutputSchema(value || "")}
                            options={{ minimap: { enabled: false }, fontSize: 13 }}
                          />
                        </Box>
                      )}

                      {/* Card View */}
                      {schemaViewMode === "card" && (
                        <Box sx={{ flex: 1, overflow: "auto", pb: 2, pr: 1 }}>
                          {(() => {
                            try {
                              const schema = JSON.parse(outputSchema || "{}");

                              // Recursive function to extract only leaf properties
                              const extractAllProperties = (obj, parentPath = "") => {
                                const allProps = [];

                                if (obj.properties) {
                                  Object.entries(obj.properties).forEach(([key, prop]) => {
                                    const fullPath = parentPath ? `${parentPath}.${key}` : key;

                                    // Only add leaf properties (no nested objects/arrays with properties)
                                    const isLeaf =
                                      !(prop.type === "object" && prop.properties) &&
                                      !(prop.type === "array" && prop.items?.properties);

                                    if (isLeaf) {
                                      allProps.push({
                                        path: fullPath,
                                        key: key,
                                        parentPath: parentPath,
                                        prop: prop,
                                        required: obj.required?.includes(key) || false,
                                      });
                                    }

                                    // Recursively get nested properties
                                    if (prop.type === "object" && prop.properties) {
                                      allProps.push(...extractAllProperties(prop, fullPath));
                                    }

                                    // Handle array items that have properties
                                    if (prop.type === "array" && prop.items?.properties) {
                                      allProps.push(
                                        ...extractAllProperties(prop.items, `${fullPath}[]`),
                                      );
                                    }
                                  });
                                }

                                return allProps;
                              };

                              const allProperties = extractAllProperties(schema);

                              return (
                                <Box
                                  sx={{ display: "flex", flexDirection: "column", gap: 1, pr: 1 }}
                                >
                                  {allProperties.map(
                                    ({ path, key, parentPath, prop, required }) => (
                                      <Paper
                                        key={path}
                                        sx={{
                                          p: 1.5,
                                          border: 1,
                                          borderColor: "divider",
                                          bgcolor: "background.paper",
                                          ml: parentPath ? parentPath.split(".").length * 2 : 0,
                                          height: 40,
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 1,
                                            width: "100%",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                              flex: 1,
                                              minWidth: 0,
                                            }}
                                          >
                                            <Typography
                                              variant="subtitle2"
                                              sx={{
                                                fontWeight: 600,
                                                fontSize: "0.8rem",
                                                flexShrink: 0,
                                              }}
                                            >
                                              {key}
                                            </Typography>
                                            {(() => {
                                              const constraints = [];
                                              const fullConstraints = [];

                                              if (prop.enum) {
                                                const enumList = prop.enum.join(", ");
                                                const maxEnumDisplay = 50;
                                                if (enumList.length > maxEnumDisplay) {
                                                  constraints.push(
                                                    `Enum: ${enumList.substring(
                                                      0,
                                                      maxEnumDisplay,
                                                    )}...`,
                                                  );
                                                  fullConstraints.push(`Enum: ${enumList}`);
                                                } else {
                                                  constraints.push(`Enum: ${enumList}`);
                                                }
                                              }
                                              if (prop.minimum !== undefined)
                                                constraints.push(`Min: ${prop.minimum}`);
                                              if (prop.maximum !== undefined)
                                                constraints.push(`Max: ${prop.maximum}`);
                                              if (prop.minLength !== undefined)
                                                constraints.push(`MinLen: ${prop.minLength}`);
                                              if (prop.maxLength !== undefined)
                                                constraints.push(`MaxLen: ${prop.maxLength}`);
                                              if (prop.pattern)
                                                constraints.push(`Pattern: ${prop.pattern}`);
                                              if (prop.format)
                                                constraints.push(`Format: ${prop.format}`);
                                              if (prop.default !== undefined)
                                                constraints.push(
                                                  `Default: ${JSON.stringify(prop.default)}`,
                                                );
                                              if (prop.items)
                                                constraints.push(
                                                  `Items: ${prop.items.type || "object"}`,
                                                );

                                              const fullText =
                                                fullConstraints.length > 0
                                                  ? fullConstraints
                                                      .concat(
                                                        constraints.slice(fullConstraints.length),
                                                      )
                                                      .join(" | ")
                                                  : constraints.join(" | ");

                                              return constraints.length > 0 ? (
                                                <>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      fontSize: "0.7rem",
                                                      color: "text.disabled",
                                                      mx: 0.5,
                                                    }}
                                                  >
                                                    |
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                      fontSize: "0.7rem",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                    title={fullText}
                                                  >
                                                    {constraints.join(", ")}
                                                  </Typography>
                                                </>
                                              ) : null;
                                            })()}
                                          </Box>
                                          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                                            <Chip
                                              label={prop.type || "any"}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                              sx={{ height: 18, fontSize: "0.65rem" }}
                                            />
                                            {required && (
                                              <Chip
                                                label="Req"
                                                size="small"
                                                color="error"
                                                sx={{ height: 18, fontSize: "0.65rem" }}
                                              />
                                            )}
                                          </Box>
                                        </Box>
                                      </Paper>
                                    ),
                                  )}
                                  {allProperties.length === 0 && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ textAlign: "center", py: 4 }}
                                    >
                                      No properties defined in schema
                                    </Typography>
                                  )}
                                </Box>
                              );
                            } catch (e) {
                              return (
                                <Alert severity="error" sx={{ m: 2 }}>
                                  Invalid JSON: {e.message}
                                </Alert>
                              );
                            }
                          })()}
                        </Box>
                      )}
                    </Box>
                  )}

                  {editorTab === 3 && (
                    <Box sx={{ maxWidth: 600 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                        Model Configuration
                      </Typography>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 500, mb: 1 }}
                          >
                            Model
                          </Typography>
                          <FormControl fullWidth>
                            <Select
                              value={modelConfig.model}
                              onChange={(e) =>
                                setModelConfig({ ...modelConfig, model: e.target.value })
                              }
                              size="small"
                            >
                              <MenuItem value="gpt-4o">gpt-4o</MenuItem>
                              <MenuItem value="gpt-4o-mini">gpt-4o-mini</MenuItem>
                              <MenuItem value="gpt-4-turbo">gpt-4-turbo</MenuItem>
                              <MenuItem value="gpt-3.5-turbo">gpt-3.5-turbo</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 500, mb: 1 }}
                          >
                            Temperature
                          </Typography>
                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            inputProps={{ min: 0, max: 2, step: 0.1 }}
                            value={modelConfig.temperature}
                            onChange={(e) =>
                              setModelConfig({
                                ...modelConfig,
                                temperature: parseFloat(e.target.value),
                              })
                            }
                            helperText="Range: 0.0 to 2.0 (higher = more creative)"
                          />
                        </Box>

                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 500, mb: 1 }}
                          >
                            Max Tokens
                          </Typography>
                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            inputProps={{ min: 100, step: 100 }}
                            value={modelConfig.max_tokens || ""}
                            onChange={(e) =>
                              setModelConfig({
                                ...modelConfig,
                                max_tokens: parseInt(e.target.value) || null,
                              })
                            }
                            helperText="Leave empty for model default"
                          />
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 500, mb: 1 }}
                          >
                            Description
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Brief description of this schema..."
                            size="small"
                          />
                        </Box>

                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 500, mb: 1 }}
                          >
                            Notes
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={5}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Additional notes, change log, or implementation details..."
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {editorTab === 4 && (
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        JSON Prompt
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Configure the JSON prompt for image generation (e.g., packaging types,
                        sizes, positioning).
                      </Typography>

                      <Box sx={{ height: 600, border: 1, borderColor: "divider", borderRadius: 1 }}>
                        <Editor
                          key={`json-prompt-${selectedSchemaType || "new"}`}
                          height="100%"
                          defaultLanguage="json"
                          defaultValue={jsonPrompt || ""}
                          onChange={(value) => setJsonPrompt(value || "")}
                          theme="vs-dark"
                          options={{
                            minimap: { enabled: false },
                            fontSize: 13,
                            lineNumbers: "on",
                            scrollBeyondLastLine: false,
                            automaticLayout: true,
                            formatOnPaste: true,
                            formatOnType: true,
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {editorTab === 5 && (
                    <Box sx={{ display: "flex", height: "100%", gap: 2 }}>
                      {/* Left Navigator - Constraint Rules by condition_json */}
                      <Paper
                        sx={{
                          width: 280,
                          borderRadius: 1,
                          border: 1,
                          borderColor: "divider",
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            p: 1.5,
                            borderBottom: 1,
                            borderColor: "divider",
                            bgcolor: "primary.main",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: "white" }}
                              >
                                Constraint Rules
                              </Typography>
                              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                                {constraintRules.length} rule
                                {constraintRules.length !== 1 ? "s" : ""}
                              </Typography>
                            </Box>
                            <Tooltip title="Add New Rule">
                              <IconButton
                                size="small"
                                sx={{ color: "white" }}
                                onClick={() => setNewRuleDialogOpen(true)}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {loadingConstraintRules ? (
                          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                            <CircularProgress size={30} />
                          </Box>
                        ) : constraintRules.length === 0 ? (
                          <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              No constraint rules found
                            </Typography>
                          </Box>
                        ) : (
                          <List sx={{ flex: 1, overflow: "auto", p: 0 }}>
                            {constraintRules.map((rule) => {
                              // Parse and format condition JSON for display
                              let conditionDisplay = rule.condition_json;
                              try {
                                const parsed = JSON.parse(rule.condition_json);
                                // Extract key-value pairs from the condition JSON
                                const entries = Object.entries(parsed);
                                if (entries.length > 0) {
                                  // Format as: field | value (e.g., "country | USA")
                                  const [key, value] = entries[0];
                                  conditionDisplay = `${key} | ${value}`;
                                }
                              } catch (e) {
                                // If parsing fails, use the raw string
                                conditionDisplay = rule.condition_json;
                              }

                              return (
                                <ListItem key={rule.rule_id} disablePadding>
                                  <ListItemButton
                                    selected={selectedConstraintRule?.rule_id === rule.rule_id}
                                    onClick={() => {
                                      setSelectedConstraintRule(rule);
                                      // Exit edit mode when selecting a different rule
                                      setEditingRule(false);
                                      setEditedRuleData(null);
                                      setEditingSection(null);
                                      setEditedConstraints({});
                                      setEditingJsonView(false);
                                      setEditedJsonContent("");
                                    }}
                                    sx={{
                                      py: 0.75,
                                      px: 2,
                                      borderLeft: 3,
                                      borderColor:
                                        selectedConstraintRule?.rule_id === rule.rule_id
                                          ? "primary.main"
                                          : "transparent",
                                    }}
                                  >
                                    <ListItemText
                                      primary={`#${rule.rule_id}: ${conditionDisplay}`}
                                      primaryTypographyProps={{
                                        variant: "body2",
                                        fontWeight:
                                          selectedConstraintRule?.rule_id === rule.rule_id
                                            ? 600
                                            : 400,
                                        fontSize: "0.85rem",
                                        sx: {
                                          overflow: "visible",
                                          textOverflow: "clip",
                                          whiteSpace: "normal",
                                          display: "block",
                                          wordBreak: "break-word",
                                        },
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        )}
                      </Paper>

                      {/* Right Content - Selected Rule Details */}
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: "1000px",
                        }}
                      >
                        {selectedConstraintRule ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              height: "100%",
                            }}
                          >
                            <Box>
                              {editingRule ? (
                                <Paper sx={{ p: 2, mb: 2, bgcolor: "action.hover" }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                      Edit Rule #{selectedConstraintRule.rule_id}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Tooltip title="Save">
                                        <IconButton color="success" onClick={saveRuleEdit}>
                                          <CheckIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Cancel">
                                        <IconButton
                                          onClick={() => {
                                            setEditingRule(false);
                                            setEditedRuleData(null);
                                          }}
                                        >
                                          <CloseIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <TextField
                                      label="Rule ID"
                                      type="number"
                                      value={editedRuleData?.rule_id || ""}
                                      onChange={(e) =>
                                        setEditedRuleData({
                                          ...editedRuleData,
                                          rule_id: parseInt(e.target.value) || 0,
                                        })
                                      }
                                      size="small"
                                      fullWidth
                                    />
                                    <TextField
                                      label="Priority"
                                      type="number"
                                      value={editedRuleData?.priority || ""}
                                      onChange={(e) =>
                                        setEditedRuleData({
                                          ...editedRuleData,
                                          priority: parseInt(e.target.value) || 0,
                                        })
                                      }
                                      size="small"
                                      fullWidth
                                      helperText="Lower number = higher precedence"
                                    />
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                      <Typography variant="body2">Active:</Typography>
                                      <Switch
                                        checked={editedRuleData?.active || false}
                                        onChange={(e) =>
                                          setEditedRuleData({
                                            ...editedRuleData,
                                            active: e.target.checked,
                                          })
                                        }
                                      />
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Condition
                                      </Typography>
                                      {editedRuleData?.condition_json &&
                                        typeof editedRuleData.condition_json === "object" && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: 1,
                                            }}
                                          >
                                            {Object.entries(editedRuleData.condition_json).map(
                                              ([key, value]) => (
                                                <Box key={key}>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{ color: "text.secondary" }}
                                                  >
                                                    {key}
                                                  </Typography>
                                                  <TextField
                                                    value={value}
                                                    onChange={(e) =>
                                                      setEditedRuleData({
                                                        ...editedRuleData,
                                                        condition_json: {
                                                          ...editedRuleData.condition_json,
                                                          [key]: e.target.value,
                                                        },
                                                      })
                                                    }
                                                    size="small"
                                                    fullWidth
                                                  />
                                                </Box>
                                              ),
                                            )}
                                          </Box>
                                        )}
                                    </Box>
                                  </Box>
                                </Paper>
                              ) : (
                                <Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 1,
                                    }}
                                  >
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                      Rule #{selectedConstraintRule.rule_id}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Tooltip title="Edit Rule">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => {
                                            // Parse condition_json if it's a string
                                            let conditionJson =
                                              selectedConstraintRule.condition_json;
                                            if (typeof conditionJson === "string") {
                                              try {
                                                conditionJson = JSON.parse(conditionJson);
                                              } catch (e) {
                                                console.error("Failed to parse condition_json:", e);
                                                conditionJson = {};
                                              }
                                            }

                                            setEditingRule(true);
                                            setEditedRuleData({
                                              rule_id: selectedConstraintRule.rule_id,
                                              priority: selectedConstraintRule.priority,
                                              active: selectedConstraintRule.active,
                                              condition_json: conditionJson,
                                            });
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete Rule">
                                        <IconButton size="small" color="error">
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                                    <Chip
                                      label={`Tower: ${selectedConstraintRule.tower}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Commodity: ${selectedConstraintRule.commodity}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Schema: ${selectedConstraintRule.schema_type}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Priority: ${selectedConstraintRule.priority}`}
                                      size="small"
                                      color="primary"
                                    />
                                  </Box>
                                  {selectedConstraintRule.notes && (
                                    <Typography variant="body2" color="text.secondary">
                                      {selectedConstraintRule.notes}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>

                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Constraints
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                  {constraintViewMode === "json" && (
                                    <>
                                      {editingJsonView ? (
                                        <>
                                          <Tooltip title="Save">
                                            <IconButton
                                              size="small"
                                              color="success"
                                              onClick={async () => {
                                                try {
                                                  // Validate JSON
                                                  const parsed = JSON.parse(editedJsonContent);

                                                  // Save to backend
                                                  const response = await apiFetch(
                                                    `${API_BASE}/constraint-rules/${selectedConstraintRule.rule_id}`,
                                                    {
                                                      method: "PUT",
                                                      headers: {
                                                        "Content-Type": "application/json",
                                                      },
                                                      body: JSON.stringify({
                                                        constraints_json: parsed,
                                                      }),
                                                    },
                                                  );

                                                  const data = await response.json();

                                                  if (data.status === "success") {
                                                    setSuccess("Constraints updated successfully");
                                                    setEditingJsonView(false);
                                                    await loadConstraintRules(selectedSchemaType);
                                                    const updatedRule = constraintRules.find(
                                                      (r) =>
                                                        r.rule_id ===
                                                        selectedConstraintRule.rule_id,
                                                    );
                                                    if (updatedRule) {
                                                      setSelectedConstraintRule(updatedRule);
                                                    }
                                                  } else {
                                                    setError(
                                                      data.error || "Failed to update constraints",
                                                    );
                                                  }
                                                } catch (error) {
                                                  setError(
                                                    `Invalid JSON or save failed: ${error.message}`,
                                                  );
                                                }
                                              }}
                                            >
                                              <CheckIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Cancel">
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                setEditingJsonView(false);
                                                setEditedJsonContent("");
                                              }}
                                            >
                                              <CloseIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      ) : (
                                        <Tooltip title="Edit JSON">
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                              setEditingJsonView(true);
                                              setEditedJsonContent(
                                                selectedConstraintRule.constraints_json,
                                              );
                                            }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </>
                                  )}
                                  <ToggleButtonGroup
                                    value={constraintViewMode}
                                    exclusive
                                    onChange={(e, newMode) =>
                                      newMode && setConstraintViewMode(newMode)
                                    }
                                    size="small"
                                  >
                                    <ToggleButton value="json">
                                      <CodeIcon fontSize="small" />
                                    </ToggleButton>
                                    <ToggleButton value="card">
                                      <ViewListIcon fontSize="small" />
                                    </ToggleButton>
                                  </ToggleButtonGroup>
                                </Box>
                              </Box>
                              {constraintViewMode === "json" ? (
                                <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                  <Box
                                    sx={{
                                      flex: 1,
                                      border: 1,
                                      borderColor: "divider",
                                      borderRadius: 1,
                                      position: "relative",
                                    }}
                                  >
                                    {editingJsonView ? (
                                      <Editor
                                        key="editing"
                                        height="100%"
                                        defaultLanguage="json"
                                        defaultValue={editedJsonContent}
                                        onChange={(value) => setEditedJsonContent(value || "")}
                                        theme="vs-dark"
                                        options={{
                                          minimap: { enabled: false },
                                          fontSize: 13,
                                          lineNumbers: "on",
                                          scrollBeyondLastLine: false,
                                          automaticLayout: true,
                                        }}
                                      />
                                    ) : (
                                      <Editor
                                        key="readonly"
                                        height="100%"
                                        defaultLanguage="json"
                                        value={selectedConstraintRule.constraints_json}
                                        theme="vs-dark"
                                        options={{
                                          readOnly: true,
                                          minimap: { enabled: false },
                                          fontSize: 13,
                                          lineNumbers: "on",
                                          scrollBeyondLastLine: false,
                                          automaticLayout: true,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ flex: 1, overflow: "auto" }}>
                                  {(() => {
                                    try {
                                      const constraints = JSON.parse(
                                        selectedConstraintRule.constraints_json,
                                      );
                                      return Object.entries(constraints).map(
                                        ([key, value], index) => (
                                          <Accordion
                                            key={index}
                                            defaultExpanded={index === 0}
                                            sx={{ mb: 0.5 }}
                                          >
                                            <AccordionSummary
                                              expandIcon={<ExpandMoreIcon />}
                                              sx={{ py: 0.5, minHeight: "40px" }}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "space-between",
                                                  alignItems: "center",
                                                  width: "100%",
                                                  mr: 1,
                                                }}
                                              >
                                                <Typography
                                                  variant="body2"
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  {key}
                                                </Typography>
                                                <Box
                                                  sx={{ display: "flex", gap: 0.5 }}
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <Tooltip title="Edit Section">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => {
                                                        setEditingSection(key);
                                                        setEditedConstraints({ ...constraints });
                                                      }}
                                                    >
                                                      <EditIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title="Delete Section">
                                                    <IconButton
                                                      size="small"
                                                      color="error"
                                                      onClick={() => {
                                                        const updated = { ...constraints };
                                                        delete updated[key];
                                                        // Here you would call an API to update the constraint
                                                        console.log("Delete section:", key);
                                                      }}
                                                    >
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                </Box>
                                              </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ py: 1, px: 2 }}>
                                              {editingSection === key ? (
                                                <Box>
                                                  {typeof value === "object" && value !== null ? (
                                                    Array.isArray(value) ? (
                                                      <Box>
                                                        {editedConstraints[key]?.map(
                                                          (item, idx) => (
                                                            <Box
                                                              key={idx}
                                                              sx={{
                                                                display: "flex",
                                                                gap: 1,
                                                                mb: 1,
                                                                alignItems: "center",
                                                              }}
                                                            >
                                                              <TextField
                                                                size="small"
                                                                fullWidth
                                                                value={item}
                                                                onChange={(e) => {
                                                                  const updated = [
                                                                    ...editedConstraints[key],
                                                                  ];
                                                                  updated[idx] = e.target.value;
                                                                  setEditedConstraints({
                                                                    ...editedConstraints,
                                                                    [key]: updated,
                                                                  });
                                                                }}
                                                              />
                                                              <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => {
                                                                  const updated = editedConstraints[
                                                                    key
                                                                  ].filter((_, i) => i !== idx);
                                                                  setEditedConstraints({
                                                                    ...editedConstraints,
                                                                    [key]: updated,
                                                                  });
                                                                }}
                                                              >
                                                                <DeleteIcon fontSize="small" />
                                                              </IconButton>
                                                            </Box>
                                                          ),
                                                        )}
                                                        <Button
                                                          size="small"
                                                          startIcon={<AddIcon />}
                                                          onClick={() => {
                                                            setEditedConstraints({
                                                              ...editedConstraints,
                                                              [key]: [
                                                                ...editedConstraints[key],
                                                                "",
                                                              ],
                                                            });
                                                          }}
                                                        >
                                                          Add Item
                                                        </Button>
                                                      </Box>
                                                    ) : (
                                                      <Box>
                                                        {Object.entries(editedConstraints[key]).map(
                                                          ([subKey, subValue]) => (
                                                            <Box key={subKey} sx={{ mb: 1.5 }}>
                                                              <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                  fontWeight: 600,
                                                                  display: "block",
                                                                  mb: 0.5,
                                                                }}
                                                              >
                                                                {subKey}
                                                              </Typography>
                                                              <TextField
                                                                size="small"
                                                                fullWidth
                                                                value={
                                                                  typeof subValue === "object"
                                                                    ? JSON.stringify(subValue)
                                                                    : subValue
                                                                }
                                                                onChange={(e) => {
                                                                  setEditedConstraints({
                                                                    ...editedConstraints,
                                                                    [key]: {
                                                                      ...editedConstraints[key],
                                                                      [subKey]: e.target.value,
                                                                    },
                                                                  });
                                                                }}
                                                              />
                                                            </Box>
                                                          ),
                                                        )}
                                                      </Box>
                                                    )
                                                  ) : (
                                                    <TextField
                                                      size="small"
                                                      fullWidth
                                                      value={editedConstraints[key]}
                                                      onChange={(e) => {
                                                        setEditedConstraints({
                                                          ...editedConstraints,
                                                          [key]: e.target.value,
                                                        });
                                                      }}
                                                    />
                                                  )}
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      gap: 1,
                                                      justifyContent: "flex-end",
                                                      mt: 2,
                                                    }}
                                                  >
                                                    <IconButton
                                                      size="small"
                                                      color="success"
                                                      onClick={() => saveConstraintSection(key)}
                                                    >
                                                      <CheckIcon />
                                                    </IconButton>
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => setEditingSection(null)}
                                                    >
                                                      <CloseIcon />
                                                    </IconButton>
                                                  </Box>
                                                </Box>
                                              ) : typeof value === "object" && value !== null ? (
                                                Array.isArray(value) ? (
                                                  <Box>
                                                    {value.map((item, idx) => (
                                                      <Typography
                                                        key={idx}
                                                        variant="body2"
                                                        sx={{ py: 0.25, pl: 1 }}
                                                      >
                                                        • {item}
                                                      </Typography>
                                                    ))}
                                                  </Box>
                                                ) : (
                                                  Object.entries(value).map(
                                                    ([subKey, subValue]) => (
                                                      <Box
                                                        key={subKey}
                                                        sx={{
                                                          display: "flex",
                                                          mb: 0.5,
                                                          alignItems: "baseline",
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body2"
                                                          color="text.secondary"
                                                          sx={{
                                                            fontWeight: 600,
                                                            minWidth: "120px",
                                                            mr: 1,
                                                          }}
                                                        >
                                                          {subKey}:
                                                        </Typography>
                                                        <Typography
                                                          variant="body2"
                                                          sx={{ flex: 1 }}
                                                        >
                                                          {Array.isArray(subValue)
                                                            ? subValue.join(", ")
                                                            : typeof subValue === "object"
                                                              ? JSON.stringify(subValue, null, 2)
                                                              : String(subValue)}
                                                        </Typography>
                                                      </Box>
                                                    ),
                                                  )
                                                )
                                              ) : (
                                                <Typography variant="body2">
                                                  {String(value)}
                                                </Typography>
                                              )}
                                            </AccordionDetails>
                                          </Accordion>
                                        ),
                                      );
                                    } catch (e) {
                                      return <Alert severity="error">Invalid JSON format</Alert>;
                                    }
                                  })()}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "100%",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Select a constraint rule to view details
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Right Panel - Versions & Audit */}
              <Box sx={{ width: 300, display: "flex", flexDirection: "column" }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, v) => setActiveTab(v)}
                  sx={{ borderBottom: 1, borderColor: "divider" }}
                >
                  <Tab label="Versions" icon={<HistoryIcon />} iconPosition="start" />
                  <Tab label="Audit Log" />
                </Tabs>

                <Box sx={{ flex: 1, overflow: "auto", p: 1.5, pb: 2 }}>
                  {activeTab === 0 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pb: 1 }}>
                      {versions.map((version) => (
                        <Paper
                          key={version.id}
                          elevation={version.is_active ? 2 : 0}
                          sx={{
                            p: 1.5,
                            border: 1,
                            borderColor: version.is_active ? "grey.600" : "divider",
                            bgcolor: version.is_active ? "grey.800" : "background.paper",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            "&:hover": {
                              borderColor: "grey.600",
                              bgcolor: "action.hover",
                            },
                          }}
                          onClick={() => {
                            if (!version.is_active) {
                              setVersionToActivate(version.schema_version);
                              setActivateDialogOpen(true);
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: version.is_active ? 600 : 400,
                                fontSize: "0.875rem",
                              }}
                            >
                              v{version.schema_version}
                            </Typography>
                            {version.is_active ? (
                              <Chip
                                label="Active"
                                size="small"
                                sx={{
                                  bgcolor: "grey.00",
                                  color: "grey.200",
                                  fontWeight: 500,
                                  fontSize: "0.7rem",
                                }}
                              />
                            ) : (
                              <Chip label="Inactive" size="small" variant="outlined" />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {new Date(version.created_at).toLocaleString()}
                          </Typography>
                          {version.created_by && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", display: "block", mt: 0.5 }}
                            >
                              By: {version.created_by}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                      {versions.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: "center", py: 2 }}
                        >
                          No versions available
                        </Typography>
                      )}
                    </Box>
                  )}

                  {activeTab === 1 && (
                    <List dense>
                      {auditLog.map((log, index) => (
                        <React.Fragment key={index}>
                          <ListItem alignItems="flex-start">
                            <ListItemText
                              primary={log.action}
                              secondary={
                                <>
                                  <Typography variant="caption" display="block">
                                    {log.created_by} • {new Date(log.created_at).toLocaleString()}
                                  </Typography>
                                  {log.description && (
                                    <Typography variant="caption" color="text.secondary">
                                      {log.description}
                                    </Typography>
                                  )}
                                </>
                              }
                            />
                          </ListItem>
                          {index < auditLog.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Select a schema type to begin editing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a tower, commodity, and schema type from the left panel
              </Typography>
            </Box>
          )}
        </Box>

        {/* Save Dialog */}
        <Dialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Save New Schema Version</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Version Number"
              type="number"
              value={newVersion}
              onChange={(e) => setNewVersion(e.target.value)}
              sx={{ mt: 2, mb: 2 }}
            />
            <TextField
              fullWidth
              label="Version Description"
              multiline
              rows={3}
              value={versionDescription}
              onChange={(e) => setVersionDescription(e.target.value)}
              placeholder="Describe the changes in this version..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSchema} variant="contained" disabled={!newVersion || saving}>
              {saving ? "Saving..." : "Save Version"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activate Version Dialog */}
        <Dialog open={activateDialogOpen} onClose={() => setActivateDialogOpen(false)}>
          <DialogTitle>Activate Version</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to activate version {versionToActivate}? This will deactivate
              the current active version.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActivateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleActivateVersion} variant="contained" color="primary">
              Activate
            </Button>
          </DialogActions>
        </Dialog>

        {/* AI Refine Dialog */}
        <Dialog
          open={refineDialogOpen}
          onClose={() => setRefineDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            AI Refine {refineType === "system" ? "System Prompt" : "User Prompt"}
          </DialogTitle>
          <DialogContent>
            {/* Current Prompt Display */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Current Prompt:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  maxHeight: 200,
                  overflow: "auto",
                  bgcolor: "background.default",
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                  whiteSpace: "pre-wrap",
                }}
              >
                {currentPromptText}
              </Paper>
            </Box>

            {/* AI Variants */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                AI-Generated Variants (select one to apply):
              </Typography>
              {loadingVariants ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Generating 3 refined variants of your prompt...
                  </Typography>
                </Box>
              ) : aiVariants.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {aiVariants.map((variant, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        border: 2,
                        borderColor: selectedVariant === variant ? "primary.main" : "divider",
                        bgcolor:
                          selectedVariant === variant ? "primary.lighter" : "background.paper",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "primary.lighter",
                        },
                      }}
                      onClick={() => setSelectedVariant(variant)}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <Box
                          sx={{
                            mt: 0.5,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            border: 2,
                            borderColor: selectedVariant === variant ? "primary.main" : "divider",
                            bgcolor: selectedVariant === variant ? "primary.main" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {selectedVariant === variant && (
                            <CheckCircleIcon sx={{ fontSize: 16, color: "white" }} />
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
                          >
                            Variant {index + 1}
                            {index === 0 && " - Concise & Clear"}
                            {index === 1 && " - Detailed & Examples"}
                            {index === 2 && " - Structured & Organized"}
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              maxHeight: 150,
                              overflow: "auto",
                              bgcolor: "background.default",
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {variant}
                          </Paper>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No variants available
                </Typography>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
              💡 Select one variant above and click "Apply Selected Variant"
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRefineDialogOpen(false)} disabled={refining}>
              Cancel
            </Button>
            <Button
              onClick={handleRefinePrompt}
              variant="contained"
              color="primary"
              disabled={!selectedVariant || refining}
              startIcon={refining ? <CircularProgress size={16} /> : <RefreshIcon />}
            >
              {refining ? "Applying..." : "Apply Selected Variant"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Fixed Position Alerts (Bottom Overlay) */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              minWidth: 300,
              maxWidth: 500,
              zIndex: 9999,
              boxShadow: 3,
            }}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            onClose={() => setSuccess(null)}
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              minWidth: 300,
              maxWidth: 500,
              zIndex: 9999,
              boxShadow: 3,
            }}
          >
            {success}
          </Alert>
        )}

        {/* New Rule Dialog */}
        <Dialog
          open={newRuleDialogOpen}
          onClose={() => setNewRuleDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create New Constraint Rule</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <TextField
                label="Priority"
                type="number"
                value={newRuleData.priority}
                onChange={(e) =>
                  setNewRuleData({ ...newRuleData, priority: parseInt(e.target.value) || 100 })
                }
                size="small"
                fullWidth
                helperText="Lower number = higher precedence"
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2">Active:</Typography>
                <Switch
                  checked={newRuleData.active}
                  onChange={(e) => setNewRuleData({ ...newRuleData, active: e.target.checked })}
                />
              </Box>

              <TextField
                label="Notes"
                value={newRuleData.notes}
                onChange={(e) => setNewRuleData({ ...newRuleData, notes: e.target.value })}
                size="small"
                fullWidth
                multiline
                rows={2}
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Condition (JSON)
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Define when this rule applies. Example:{" "}
                  {JSON.stringify({ marketPositioning: "Premium" })}
                </Typography>
                <TextField
                  value={JSON.stringify(newRuleData.condition_json, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setNewRuleData({ ...newRuleData, condition_json: parsed });
                    } catch (err) {
                      // Invalid JSON, don't update
                    }
                  }}
                  multiline
                  rows={4}
                  fullWidth
                  size="small"
                  sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Constraints (JSON)
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Define the constraints to apply. Example:{" "}
                  {JSON.stringify({ required: ["field1"], enum: { status: ["active"] } })}
                </Typography>
                <TextField
                  value={JSON.stringify(newRuleData.constraints_json, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setNewRuleData({ ...newRuleData, constraints_json: parsed });
                    } catch (err) {
                      // Invalid JSON, don't update
                    }
                  }}
                  multiline
                  rows={8}
                  fullWidth
                  size="small"
                  sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewRuleDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={createNewRule}>
              Create Rule
            </Button>
          </DialogActions>
        </Dialog>

        {/* Execution Group Management Dialog */}
        <Dialog
          open={executionGroupDialogOpen}
          onClose={() => !savingGroups && setExecutionGroupDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">Manage Execution Groups</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={autoAssignGroups}
                disabled={savingGroups}
              >
                Auto-Assign Groups
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reorder schemas and assign execution groups. Schemas in the same group execute in
              parallel.
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={80}>Order</TableCell>
                    <TableCell>Schema Type</TableCell>
                    <TableCell width={120}>Group</TableCell>
                    <TableCell width={100} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tempSchemaGroups.map((schema, index) => (
                    <TableRow key={schema.schema_type}>
                      <TableCell>
                        <Chip
                          label={index + 1}
                          size="small"
                          sx={{
                            minWidth: 40,
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {schema.schema_type}
                        </Typography>
                        {schema.description && (
                          <Typography variant="caption" color="text.secondary">
                            {schema.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={schema.execution_group}
                          onChange={(e) => handleGroupChange(schema.schema_type, e.target.value)}
                          size="small"
                          inputProps={{ min: 1, max: 99 }}
                          disabled={savingGroups}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                          <IconButton
                            size="small"
                            onClick={() => moveSchemaUp(index)}
                            disabled={index === 0 || savingGroups}
                          >
                            <ExpandMoreIcon sx={{ transform: "rotate(180deg)" }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveSchemaDown(index)}
                            disabled={index === tempSchemaGroups.length - 1 || savingGroups}
                          >
                            <ExpandMoreIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2, p: 2, bgcolor: "info.lighter", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                💡 Tip: Schemas are executed by group number (ascending). Schemas in the same group
                run in parallel. Use the up/down arrows to reorder, or change group numbers
                directly.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExecutionGroupDialogOpen(false)} disabled={savingGroups}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveExecutionGroups}
              disabled={savingGroups}
              startIcon={savingGroups ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {savingGroups ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SidebarLayout>
  );
};

export default SchemaManagementV2Page;
